import { Visibility, VisibilityOff } from '@mui/icons-material'
import { alpha, Box, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import { RefObject, useState } from 'react'
import { useTranslation } from 'react-i18next'

export interface PasswordFieldProps {
  id: string
  name: string
  label: string
  // Basis for the data-cy attributes: `text-${cy}` on the field, `error-${cy}` on the error text
  // and `icon-Toggle ${cy}` on the show/hide button.
  cy: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  // Translated error text. When set, the field is in error state and the text is shown below it.
  error?: string | null
  autoComplete?: string
  inputRef?: RefObject<HTMLInputElement>
  color?: 'primary' | 'secondary'
}

// Opacity of `error.main` laid over the field when the password is invalid. At 0.2 the text in
// `error.light` still has a 4.5:1 contrast ratio against the tinted field (WCAG AA); more tint
// than that and the red text becomes harder to read than it is on the normal grey field.
const ERROR_TINT = 0.2

/**
 * Password input whose validation error is shown in the field itself: the whole field turns red
 * (background, text and outline) and the requirement is printed right under it in a size that is
 * readable on a phone, instead of only as a toast in the corner of the screen.
 *
 * The eye button at the end of the field reveals the typed password so it can be checked on a
 * phone keyboard. It is left out of the tab order so the keyboard goes field to field, and the
 * revealed state lives only in this component, so it is never kept across page loads.
 */
export const PasswordField = (props: PasswordFieldProps): JSX.Element => {

  const { t } = useTranslation()
  const [visible, setVisible] = useState(false)
  const errorId = `${props.id}-error`

  return (
    <Box>
      <TextField
        margin="normal"
        fullWidth
        type={visible ? 'text' : 'password'}
        id={props.id}
        name={props.name}
        label={props.label}
        value={props.value}
        error={!!props.error}
        color={props.color}
        autoComplete={props.autoComplete}
        inputRef={props.inputRef}
        inputProps={props.error ? { 'aria-describedby': errorId } : undefined}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={visible ? t('Hide password') : t('Show password')}
                aria-pressed={visible}
                onClick={() => setVisible(old => !old)}
                // Keep the focus (and the phone keyboard) in the field while toggling.
                onMouseDown={e => e.preventDefault()}
                edge="end"
                tabIndex={-1}
                data-cy={`icon-Toggle ${props.cy}`}
              >
                {visible ? <VisibilityOff/> : <Visibility/>}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root.Mui-error': {
            backgroundColor: theme => alpha(theme.palette.error.main, ERROR_TINT),
            color: 'error.light',
            '& .MuiIconButton-root': {
              color: 'error.light',
            },
          },
          '& .MuiInputLabel-root.Mui-error': {
            color: 'error.light',
          },
        }}
        onChange={e => props.onChange(e.target.value)}
        onBlur={props.onBlur}
        data-cy={`text-${props.cy}`}
      />
      {props.error &&
        <Typography
          id={errorId}
          role="alert"
          data-cy={`error-${props.cy}`}
          sx={{
            color: 'error.light',
            fontSize: '1rem',
            fontWeight: 600,
            lineHeight: 1.4,
            mt: -0.5,
            mb: 1,
            overflowWrap: 'anywhere',
          }}
        >
          {props.error}
        </Typography>
      }
    </Box>
  )
}
